import type { ModuleKey, PermissionKey } from '@solar/types';

/**
 * The single registry of application modules and the navigation tree they form.
 *
 * Sidebar navigation, breadcrumb labels, route guards and the command palette are
 * all derived from this file — adding a module means adding one entry here, not
 * editing five files. Nothing in here imports React or touches the DOM, so the API
 * can read the same registry when it needs a module's label.
 */

export type NavGroupKey =
  | 'overview'
  | 'crm'
  | 'epc'
  | 'service'
  | 'distribution'
  | 'finance'
  | 'people'
  | 'business'
  | 'settings';

export interface NavGroup {
  key: NavGroupKey;
  label: string;
  /** lucide-react icon name; resolved to a component in the web app. */
  icon: string;
  order: number;
  /**
   * Renders its modules as top-level entries with no heading and no collapse.
   * Used for the single-item sections — Dashboard, Tenders, Reports — where a
   * group header would be a wrapper around one link.
   */
  standalone?: boolean;
}

export const NAV_GROUPS: NavGroup[] = [
  { key: 'overview', label: 'Overview', icon: 'LayoutDashboard', order: 1, standalone: true },
  { key: 'crm', label: 'CRM', icon: 'Contact', order: 2 },
  { key: 'epc', label: 'EPC', icon: 'HardHat', order: 3 },
  { key: 'service', label: 'Service', icon: 'Wrench', order: 4 },
  { key: 'distribution', label: 'Distribution', icon: 'Warehouse', order: 5 },
  { key: 'finance', label: 'Finance', icon: 'Banknote', order: 6 },
  { key: 'people', label: 'People', icon: 'UsersRound', order: 7 },
  { key: 'business', label: 'Business', icon: 'Gavel', order: 8, standalone: true },
  { key: 'settings', label: 'Settings', icon: 'Settings', order: 9 },
];

export interface ModuleDefinition {
  /** Matches the RBAC module key so guards can be derived automatically. */
  key: ModuleKey;
  label: string;
  /** Singular noun used in page titles, toasts and confirm dialogs. */
  singular: string;
  path: string;
  /** lucide-react icon name; resolved to a component in the web app. */
  icon: string;
  group: NavGroupKey;
  order: number;
  /** Permission required to see the nav entry and load the route. */
  permission: PermissionKey;
  description: string;
  /** Hidden from the sidebar but still routable (reached from a parent screen). */
  hiddenInNav?: boolean;
}

export const MODULES: ModuleDefinition[] = [
  /* ---------------------------------------------------------------- */
  /* Overview                                                          */
  /* ---------------------------------------------------------------- */
  {
    key: 'dashboard',
    label: 'Dashboard',
    singular: 'Dashboard',
    path: '/dashboard',
    icon: 'LayoutDashboard',
    group: 'overview',
    order: 1,
    permission: 'dashboard:view',
    description: 'Company-wide KPIs, sales pipeline and execution health',
  },

  /* ---------------------------------------------------------------- */
  /* CRM                                                               */
  /* ---------------------------------------------------------------- */
  {
    key: 'leads',
    label: 'Leads',
    singular: 'Lead',
    path: '/crm/leads',
    icon: 'Sparkles',
    group: 'crm',
    order: 1,
    permission: 'leads:view',
    description: 'Enquiry capture, qualification and assignment',
  },
  {
    key: 'customers',
    label: 'Customers',
    singular: 'Customer',
    path: '/crm/customers',
    icon: 'Users',
    group: 'crm',
    order: 2,
    permission: 'customers:view',
    description: 'Converted accounts, sites and contact history',
  },
  {
    key: 'crm',
    label: 'Activities',
    singular: 'Activity',
    path: '/activities',
    icon: 'Activity',
    group: 'crm',
    order: 3,
    permission: 'crm:view',
    description: 'Unified call, visit and follow-up feed across every record',
  },
  {
    key: 'referrals',
    label: 'Referrals',
    singular: 'Referral',
    path: '/referrals',
    icon: 'Share2',
    group: 'crm',
    order: 4,
    permission: 'referrals:view',
    description: 'Referrer registry, conversions and commission payouts',
  },
  /**
   * The Communication Centre.
   *
   * Under CRM rather than Settings, and that placement is the argument for it
   * being a module of its own. What was said to a customer, and whether it
   * reached them, is a sales and service question asked by the people who deal
   * with that customer — not an administration question asked by whoever
   * configures the wire. `/integrations` keeps the wire; this keeps the
   * conversation.
   *
   * Templates are configured here rather than there for the same reason: the
   * person who decides what the company says to a customer at handover is not
   * the person who holds the Meta access token.
   */
  {
    key: 'communications',
    label: 'Communications',
    singular: 'Message',
    path: '/communications',
    icon: 'MessagesSquare',
    group: 'crm',
    order: 5,
    permission: 'communications:view',
    description: 'Every message sent to a customer, the templates behind them and what failed',
  },

  /* ---------------------------------------------------------------- */
  /* EPC                                                               */
  /* ---------------------------------------------------------------- */
  {
    key: 'projects',
    label: 'Projects',
    singular: 'Project',
    path: '/epc/projects',
    icon: 'FolderKanban',
    group: 'epc',
    order: 1,
    permission: 'projects:view',
    description: 'End-to-end tracking through the configurable EPC pipeline',
  },
  {
    key: 'site_surveys',
    label: 'Site Surveys',
    singular: 'Site Survey',
    path: '/epc/site-surveys',
    icon: 'ClipboardCheck',
    group: 'epc',
    order: 2,
    permission: 'site_surveys:view',
    description: 'Rooftop feasibility, shadow analysis and load assessment',
  },
  {
    key: 'system_designs',
    label: 'System Designs',
    singular: 'System Design',
    path: '/epc/system-designs',
    icon: 'DraftingCompass',
    group: 'epc',
    order: 3,
    permission: 'system_designs:view',
    description: 'Array layout, inverter sizing, drawings and yield estimate',
  },
  {
    key: 'quotations',
    label: 'Quotations',
    singular: 'Quotation',
    path: '/epc/quotations',
    icon: 'FileText',
    group: 'epc',
    order: 4,
    permission: 'quotations:view',
    description: 'Costed proposals, revisions and approvals',
  },
  {
    key: 'customer_approvals',
    label: 'Customer Approvals',
    singular: 'Customer Approval',
    path: '/epc/customer-approvals',
    icon: 'BadgeCheck',
    group: 'epc',
    order: 5,
    permission: 'customer_approvals:view',
    description: 'How and when the customer accepted, and the signed proof',
  },
  {
    key: 'work_orders',
    label: 'Work Orders',
    singular: 'Work Order',
    path: '/work-orders',
    icon: 'FileCheck2',
    group: 'epc',
    order: 6,
    permission: 'work_orders:view',
    description: 'Confirmed orders handed over to execution',
  },
  {
    key: 'material_planning',
    label: 'Material Planning',
    singular: 'Material Plan',
    path: '/epc/material-planning',
    icon: 'ListChecks',
    group: 'epc',
    order: 7,
    permission: 'material_planning:view',
    description: 'Required against available, with the shortage to procure',
  },
  {
    key: 'fabrication',
    label: 'Fabrication',
    singular: 'Fabrication Job',
    path: '/epc/fabrication',
    icon: 'Hammer',
    group: 'epc',
    order: 8,
    permission: 'fabrication:view',
    description: 'Custom structure work and the stock it consumes',
  },
  {
    key: 'installation',
    label: 'Installation',
    singular: 'Installation',
    path: '/installation',
    icon: 'Wrench',
    group: 'epc',
    order: 9,
    permission: 'installation:view',
    description: 'Site execution, crew scheduling and QC checklists',
  },
  {
    key: 'electrical_testing',
    label: 'Electrical Testing',
    singular: 'Test Report',
    path: '/epc/electrical-testing',
    icon: 'Gauge',
    group: 'epc',
    order: 10,
    permission: 'electrical_testing:view',
    description: 'Insulation, earthing, polarity and protection readings',
  },
  {
    key: 'net_metering',
    label: 'Net Metering',
    singular: 'Net Meter Application',
    path: '/epc/net-metering',
    icon: 'Gauge',
    group: 'epc',
    order: 11,
    permission: 'net_metering:view',
    description: 'Meter application, fee, allotment and installation',
  },
  {
    key: 'discom',
    label: 'DISCOM',
    singular: 'DISCOM Application',
    path: '/discom',
    icon: 'PlugZap',
    group: 'epc',
    order: 12,
    permission: 'discom:view',
    description: 'Connection applications, feasibility and utility inspection',
  },
  {
    key: 'commissioning',
    label: 'Commissioning',
    singular: 'Commissioning',
    path: '/commissioning',
    icon: 'Zap',
    group: 'epc',
    order: 13,
    permission: 'commissioning:view',
    description: 'Inspection, grid synchronisation and handover',
  },
  {
    key: 'subsidy',
    label: 'Subsidy',
    singular: 'Subsidy Claim',
    path: '/subsidy',
    icon: 'Sun',
    group: 'epc',
    order: 14,
    permission: 'subsidy:view',
    description: 'PM Surya Ghar registration, claims and disbursement',
  },
  {
    key: 'project_closure',
    label: 'Project Closure',
    singular: 'Project Closure',
    path: '/epc/project-closure',
    icon: 'PackageCheck',
    group: 'epc',
    order: 15,
    permission: 'project_closure:view',
    description: 'Handover checklist, completion certificate and sign-off',
  },
  {
    key: 'warranties',
    label: 'Warranties',
    singular: 'Warranty',
    path: '/epc/warranties',
    icon: 'ShieldCheck',
    group: 'epc',
    order: 16,
    permission: 'warranties:view',
    description: 'What is covered on each delivered system, and until when',
  },

  /* ---------------------------------------------------------------- */
  /* Service                                                           */
  /* ---------------------------------------------------------------- */
  {
    key: 'service_dashboard',
    label: 'Service Overview',
    singular: 'Overview',
    path: '/service',
    icon: 'LifeBuoy',
    group: 'service',
    order: 0,
    permission: 'service_dashboard:view',
    description: 'Open and overdue tickets, today’s visits and how long repairs are taking',
  },
  {
    key: 'service',
    label: 'Service Tickets',
    singular: 'Service Ticket',
    path: '/service/tickets',
    icon: 'Wrench',
    group: 'service',
    order: 1,
    permission: 'service:view',
    description: 'Complaints and breakdowns on delivered systems, and who is on them',
  },
  {
    key: 'amc',
    label: 'AMC Contracts',
    singular: 'AMC Contract',
    path: '/service/amc',
    icon: 'CalendarCheck',
    group: 'service',
    order: 2,
    permission: 'amc:view',
    description: 'Maintenance contracts, their visit schedule and what is due for renewal',
  },

  /* ---------------------------------------------------------------- */
  /* Distribution                                                      */
  /* ---------------------------------------------------------------- */
  {
    key: 'vendors',
    label: 'Vendors',
    singular: 'Vendor',
    path: '/purchase/vendors',
    icon: 'Factory',
    group: 'distribution',
    order: 1,
    permission: 'vendors:view',
    description: 'Supplier master with purchase history and outstanding',
  },
  {
    key: 'purchase_requisitions',
    label: 'Requisitions',
    singular: 'Purchase Requisition',
    path: '/purchase/requisitions',
    icon: 'ClipboardList',
    group: 'distribution',
    order: 2,
    permission: 'purchase_requisitions:view',
    description: 'Internal material requests awaiting sourcing',
  },
  {
    key: 'purchase_orders',
    label: 'Purchase Orders',
    singular: 'Purchase Order',
    path: '/purchase/orders',
    icon: 'ShoppingCart',
    group: 'distribution',
    order: 3,
    permission: 'purchase_orders:view',
    description: 'Supplier ordering with approval workflow',
  },
  {
    key: 'goods_receipt',
    label: 'Goods Receipt',
    singular: 'Goods Receipt',
    path: '/purchase/grn',
    icon: 'PackageCheck',
    group: 'distribution',
    order: 4,
    permission: 'goods_receipt:view',
    description: 'Inward material, QC and stock posting',
  },
  {
    key: 'purchase_returns',
    label: 'Purchase Returns',
    singular: 'Purchase Return',
    path: '/purchase/returns',
    icon: 'Undo2',
    group: 'distribution',
    order: 5,
    permission: 'purchase_returns:view',
    description: 'Material going back to the supplier',
  },
  {
    key: 'ftr_uploads',
    label: 'FTR',
    singular: 'FTR Upload',
    path: '/ftr',
    icon: 'FileSpreadsheet',
    group: 'distribution',
    order: 6,
    permission: 'ftr_uploads:view',
    description: 'Flash Test Report ingestion and serial extraction',
  },
  {
    // Sits with FTR because a serial only exists as the output of one, and the
    // two screens are read together when a panel has to be traced.
    key: 'serials',
    label: 'Serial Tracking',
    singular: 'Serial Number',
    path: '/serials',
    icon: 'ScanBarcode',
    group: 'distribution',
    order: 7,
    permission: 'serials:view',
    description: 'Panel serials from goods receipt to rooftop or dealer',
  },
  {
    key: 'inventory',
    label: 'Inventory',
    singular: 'Stock Item',
    path: '/inventory',
    icon: 'Boxes',
    group: 'distribution',
    order: 8,
    permission: 'inventory:view',
    description: 'Warehouse-wise stock, movement ledger and valuation',
  },
  {
    key: 'stock_allocations',
    label: 'Allocations',
    singular: 'Allocation',
    path: '/inventory/allocations',
    icon: 'PackageOpen',
    group: 'distribution',
    order: 9,
    permission: 'stock_allocations:view',
    description: 'Stock held against a project, from reservation to consumption',
  },
  {
    key: 'stock_transfers',
    label: 'Stock Transfers',
    singular: 'Transfer',
    path: '/inventory/transfers',
    icon: 'ArrowLeftRight',
    group: 'distribution',
    order: 10,
    permission: 'stock_transfers:view',
    description: 'Warehouse-to-warehouse movement with an in-transit leg',
  },
  {
    key: 'stock_adjustments',
    label: 'Stock Adjustments',
    singular: 'Adjustment',
    path: '/inventory/adjustments',
    icon: 'SlidersHorizontal',
    group: 'distribution',
    order: 11,
    permission: 'stock_adjustments:view',
    description: 'Damage, loss, count differences and opening balances',
  },
  {
    key: 'dealers',
    label: 'Dealers',
    singular: 'Dealer',
    path: '/dealers',
    icon: 'Store',
    group: 'distribution',
    order: 12,
    permission: 'dealers:view',
    description: 'Channel partner onboarding, KYC and performance',
  },
  {
    key: 'dealer_pricing',
    label: 'Dealer Pricing',
    singular: 'Price List',
    path: '/dealer-pricing',
    icon: 'Tags',
    group: 'distribution',
    order: 13,
    permission: 'dealer_pricing:view',
    description: 'Tier-based price lists with validity windows',
  },
  {
    key: 'proforma_invoices',
    label: 'Proforma Invoices',
    singular: 'Proforma Invoice',
    path: '/proforma-invoices',
    icon: 'ReceiptText',
    group: 'distribution',
    order: 14,
    permission: 'proforma_invoices:view',
    description: 'Advance billing against dealer and customer orders',
  },
  {
    key: 'dispatch',
    label: 'Dispatch',
    singular: 'Dispatch',
    path: '/dispatch',
    icon: 'Truck',
    group: 'distribution',
    order: 15,
    permission: 'dispatch:view',
    description: 'Outward material with serial handover and e-way details',
  },
  {
    key: 'dcr_compliance',
    label: 'DCR',
    singular: 'DCR Record',
    path: '/dcr',
    icon: 'BadgeCheck',
    group: 'distribution',
    order: 16,
    permission: 'dcr_compliance:view',
    description: 'Domestic Content Requirement certificates and verification',
  },

  /* ---------------------------------------------------------------- */
  /* Finance                                                           */
  /* ---------------------------------------------------------------- */
  {
    key: 'finance',
    label: 'Finance Overview',
    singular: 'Overview',
    path: '/finance',
    icon: 'Landmark',
    group: 'finance',
    order: 1,
    permission: 'finance:view',
    description: 'Receivable, payable, collections and integration health',
  },
  {
    key: 'sales_invoices',
    label: 'Invoices',
    singular: 'Invoice',
    path: '/finance/invoices',
    icon: 'ReceiptText',
    group: 'finance',
    order: 2,
    permission: 'sales_invoices:view',
    description: 'GST tax invoices, issued against projects and supply',
  },
  {
    key: 'payments',
    label: 'Payments',
    singular: 'Payment',
    path: '/finance/payments',
    icon: 'IndianRupee',
    group: 'finance',
    order: 3,
    permission: 'payments:view',
    description: 'Money in and out, reconciled against documents',
  },
  {
    key: 'receivables',
    label: 'Receivables',
    singular: 'Receivable',
    path: '/finance/receivables',
    icon: 'ArrowDownLeft',
    group: 'finance',
    order: 4,
    permission: 'receivables:view',
    description: 'What customers owe, aged by how long it has been owed',
  },
  {
    key: 'payables',
    label: 'Payables',
    singular: 'Payable',
    path: '/finance/payables',
    icon: 'ArrowUpRight',
    group: 'finance',
    order: 5,
    permission: 'payables:view',
    description: 'What is owed to suppliers, aged against the order',
  },
  {
    key: 'expenses',
    label: 'Expenses',
    singular: 'Expense',
    path: '/finance/expenses',
    icon: 'Wallet',
    group: 'finance',
    order: 6,
    permission: 'expenses:view',
    description: 'Site, travel and overhead spend with approvals',
  },
  {
    key: 'payment_followups',
    label: 'Payment Follow-ups',
    singular: 'Follow-up',
    path: '/finance/followups',
    icon: 'PhoneCall',
    group: 'finance',
    order: 7,
    permission: 'payment_followups:view',
    description: 'Promises to pay, escalation and collection calls',
  },
  // Integrations used to sit here as "Tally Sync". It moved to Settings when the
  // framework grew to nine providers — see the entry in that group. The
  // accounting queue is still one tab of it, and the Finance tab strip still
  // links straight to that tab, so an accountant's path to it is unchanged.
  /* ---------------------------------------------------------------- */
  /* People — HR & employee management                                 */
  /* ---------------------------------------------------------------- */
  {
    key: 'hr',
    label: 'HR Overview',
    singular: 'Overview',
    path: '/hr',
    icon: 'UsersRound',
    group: 'people',
    order: 1,
    permission: 'hr:view',
    description: 'Headcount, today’s attendance, pending leave and open tasks',
  },
  {
    key: 'hr_employees',
    label: 'Employees',
    singular: 'Employee',
    path: '/hr/employees',
    icon: 'IdCard',
    group: 'people',
    order: 2,
    permission: 'hr_employees:view',
    description: 'The employee master — identity, reporting line and documents',
  },
  {
    key: 'hr_attendance',
    label: 'Attendance',
    singular: 'Attendance Record',
    path: '/hr/attendance',
    icon: 'CalendarCheck',
    group: 'people',
    order: 3,
    permission: 'hr_attendance:view',
    description: 'Daily register, regular and GPS-marked, with working hours',
  },
  {
    key: 'hr_leaves',
    label: 'Leave',
    singular: 'Leave Request',
    path: '/hr/leaves',
    icon: 'CalendarOff',
    group: 'people',
    order: 4,
    permission: 'hr_leaves:view',
    description: 'Requests, manager decisions and running balances',
  },
  {
    key: 'hr_payroll',
    label: 'Payroll',
    singular: 'Salary Structure',
    path: '/hr/payroll',
    icon: 'Wallet',
    group: 'people',
    order: 5,
    permission: 'hr_payroll:view',
    description: 'Effective-dated salary structures and bank details',
  },
  {
    key: 'hr_tasks',
    label: 'Tasks',
    singular: 'Task',
    path: '/hr/tasks',
    icon: 'ListChecks',
    group: 'people',
    order: 6,
    permission: 'hr_tasks:view',
    description: 'Work assigned to employees, against a project or standalone',
  },
  {
    key: 'hr_performance',
    label: 'Performance',
    singular: 'Review',
    path: '/hr/performance',
    icon: 'Target',
    group: 'people',
    order: 7,
    permission: 'hr_performance:view',
    description: 'Period reviews built from task, attendance and project data',
  },

  /* ---------------------------------------------------------------- */
  /* Business — standalone top-level entries                           */
  /* ---------------------------------------------------------------- */
  {
    key: 'tenders',
    label: 'Tenders',
    singular: 'Tender',
    path: '/tenders',
    icon: 'Gavel',
    group: 'business',
    order: 1,
    permission: 'tenders:view',
    description: 'Government and institutional bids with EMD tracking',
  },
  {
    key: 'reports',
    label: 'Reports',
    singular: 'Report',
    path: '/reports',
    icon: 'BarChart3',
    group: 'business',
    order: 2,
    permission: 'reports:view',
    description: 'Sales, execution, inventory and financial reporting',
  },

  /* ---------------------------------------------------------------- */
  /* Settings                                                          */
  /* ---------------------------------------------------------------- */
  {
    key: 'company_settings',
    label: 'Company',
    singular: 'Setting',
    path: '/settings',
    icon: 'Building2',
    group: 'settings',
    order: 1,
    permission: 'company_settings:view',
    description: 'Company profile, branches, numbering series and preferences',
  },
  {
    key: 'users',
    label: 'Users',
    singular: 'User',
    path: '/users',
    icon: 'UserCog',
    group: 'settings',
    order: 2,
    permission: 'users:view',
    description: 'Employee accounts, reporting lines and access',
  },
  {
    key: 'roles',
    label: 'Roles & Permissions',
    singular: 'Role',
    path: '/roles',
    icon: 'ShieldCheck',
    group: 'settings',
    order: 3,
    permission: 'roles:view',
    description: 'Role definitions and the permission matrix',
  },
  /**
   * External integrations.
   *
   * Moved out of Finance when the framework grew past Tally. Nine providers —
   * accounting, three lead channels, WhatsApp, email, calendar, payments and
   * SMS — configuring which are on, where their webhooks arrive and what has
   * failed. That is administration, not accounting, and an accountant reaching
   * the Tally queue still gets there from the Finance tab strip.
   *
   * The path is `/integrations` rather than `/settings/integrations` on
   * purpose: `company_settings` owns `/settings/*` as a splat and is registered
   * ahead of this entry, so a nested path would be swallowed by it and would
   * demand `company_settings:view` to open.
   */
  {
    key: 'integrations',
    label: 'Integrations',
    singular: 'Integration',
    path: '/integrations',
    icon: 'Plug',
    group: 'settings',
    order: 4,
    permission: 'integrations:view',
    description: 'External systems — connections, webhooks, message templates and failures',
  },
  {
    key: 'master_data',
    label: 'Master Data',
    singular: 'Master Record',
    path: '/master-data',
    icon: 'Database',
    group: 'settings',
    order: 5,
    permission: 'master_data:view',
    description: 'Products, suppliers, warehouses, DISCOMs and lookups',
  },
  {
    key: 'notifications',
    label: 'Notifications',
    singular: 'Notification',
    path: '/notifications',
    icon: 'Bell',
    group: 'settings',
    order: 6,
    permission: 'notifications:view',
    description: 'In-app alerts, reminders and approval requests',
  },
  {
    key: 'audit_logs',
    label: 'Audit Logs',
    singular: 'Audit Entry',
    path: '/audit-logs',
    icon: 'History',
    group: 'settings',
    order: 6,
    permission: 'audit_logs:view',
    description: 'Immutable record of every change made in the system',
  },
];

const moduleByKey = new Map(MODULES.map((m) => [m.key, m]));

export const getModule = (key: ModuleKey): ModuleDefinition | undefined => moduleByKey.get(key);

export const getModulesByGroup = (group: NavGroupKey): ModuleDefinition[] =>
  MODULES.filter((m) => m.group === group).sort((a, b) => a.order - b.order);

/** Longest-prefix match, so `/leads/:id/edit` still resolves to the Leads module. */
export const getModuleByPath = (pathname: string): ModuleDefinition | undefined =>
  MODULES.filter((m) => pathname === m.path || pathname.startsWith(`${m.path}/`)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];

export const getNavGroup = (key: NavGroupKey): NavGroup | undefined =>
  NAV_GROUPS.find((group) => group.key === key);

/* ------------------------------------------------------------------ */
/* Navigation tree                                                     */
/* ------------------------------------------------------------------ */

export interface NavItemNode {
  type: 'item';
  module: ModuleDefinition;
}

export interface NavGroupNode {
  type: 'group';
  group: NavGroup;
  modules: ModuleDefinition[];
}

export type NavNode = NavItemNode | NavGroupNode;

/**
 * The sidebar tree, filtered by whatever the caller counts as visible.
 *
 * Filtering happens here rather than in the component so an empty group cannot be
 * rendered: a sales executive should never see a "Settings" heading that expands
 * to nothing, which would only advertise what they cannot reach.
 *
 * `isVisible` receives the module rather than a permission key so the caller keeps
 * ownership of the RBAC decision — the registry has no opinion on how access is
 * evaluated.
 */
export const buildNavTree = (isVisible: (module: ModuleDefinition) => boolean): NavNode[] =>
  [...NAV_GROUPS]
    .sort((a, b) => a.order - b.order)
    .flatMap<NavNode>((group) => {
      const modules = getModulesByGroup(group.key).filter(
        (module) => !module.hiddenInNav && isVisible(module),
      );

      if (modules.length === 0) return [];

      return group.standalone
        ? modules.map((module) => ({ type: 'item', module }))
        : [{ type: 'group', group, modules }];
    });

/** The group a route belongs to — used to auto-expand the sidebar on navigation. */
export const getNavGroupByPath = (pathname: string): NavGroupKey | undefined =>
  getModuleByPath(pathname)?.group;
